import React, { useEffect, useMemo, useRef, useState } from 'react';
import { checkSparkDataAvailable } from 'src/helpers/validationHelper';
import { variables } from 'src/Tenants';
import { SuiteDummyData, SuiteHeaders } from 'src/common/constants';
import CSVUpload from 'src/components/CSVUpload/CSVUpload';
import { CSVLink } from 'react-csv';
import { usePropertyRequestDataActionsFromContext } from 'src/pages/Property/Context/PropertyContext';
import { IndividualSuiteParsedFromCSV } from 'src/pages/Property/CustomHooks/UseBulkSaveSuite/BulkSaveSuiteAPITypes';
import UseFetchInventoryUnitsForExport from 'src/SharedCustomHooks/UseFetchInventoryUnitsForExport/UseFetchInventoryUnitsForExport';

interface ActionOption { value: string; label: string; disabled?: boolean }

const filterActionOptionsByRole = (
  userRole: string,
  allOptions: Array<ActionOption>
): ActionOption[] => {
  if (userRole === 'Admin') {
    return allOptions;
  }

  return allOptions.filter(
    (option: ActionOption) => option.value !== 'bulk-import' && option.value !== 'bulk-export'
  );
};

const SuitesActionSelect = ({
  user,
  property,
  onAddSuite,
  handleOpenBulkWalkthroughsDialogue,
  handleOpenBulkKeyPickupDialogue,
  fetchPropertySuites,
}: any) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [csvUploadKey, setCsvUploadKey] = useState(0);
  const [shouldTriggerUpload, setShouldTriggerUpload] = useState(false);

  const userRole = user?.roles[0].role?.name;

  const csvDownloadRef = useRef<any>(null);
  const csvUploadRef = useRef<any>(null);

  const { fetchSaveBulkSuitesResponse } =
    usePropertyRequestDataActionsFromContext();

  const { retrieveLatestInventoryUnitsFromAPI } =
    UseFetchInventoryUnitsForExport();

  useEffect(() => {
    if (shouldTriggerUpload && csvUploadRef.current) {
      const fileInput: any =
        csvUploadRef.current.querySelector('input[type="file"]');

      if (fileInput) {
        fileInput.click();
      }

      setShouldTriggerUpload(false);
    }
  }, [csvUploadKey, shouldTriggerUpload]);

  const shouldHideButtons: boolean =
    checkSparkDataAvailable(property, user.roles[0].role.name) ||
    user.roles[0].role.name === 'Home Owner' ||
    user.roles[0].role.name === 'Occupant' ||
    user.roles[0].role.name === 'Strata Manager';

  if (shouldHideButtons) return null;

  const allActionOptions: ActionOption[] = [
    { value: '', label: 'Quick Actions...', disabled: true },
    { value: 'download-csv', label: 'Download Import Template' },
    { value: 'bulk-import', label: 'Import Units' },
    { value: 'bulk-export', label: 'Export Units' },
    { value: 'add-suite', label: `Add ${variables.Suite}` },
    { value: 'bulk-walkthroughs', label: 'Add Walkthroughs' },
    { value: 'bulk-key-pickup', label: 'Configure Key Pickups' },
  ];

  const filteredActionOptionsByRole: ActionOption[] = useMemo(
    () => filterActionOptionsByRole(userRole, allActionOptions),
    [userRole]
  );

  const handleActionChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const action = e.target.value;

    switch (action) {
      case 'bulk-export':
        await retrieveLatestInventoryUnitsFromAPI(property.id);
        break;
      case 'download-csv':
        triggerCSVDownload();
        break;
      case 'bulk-import':
        triggerCSVUpload();
        break;
      case 'add-suite':
        onAddSuite();
        break;
      case 'bulk-walkthroughs':
        handleOpenBulkWalkthroughsDialogue();
        break;
      case 'bulk-key-pickup':
        handleOpenBulkKeyPickupDialogue();
        break;
      default:
        break;
    }

    setSelectedAction('');
  };

  const triggerCSVDownload = () => {
    if (csvDownloadRef.current?.link) {
      csvDownloadRef.current.link.click();
    }
  };

  const triggerCSVUpload = () => {
    setCsvUploadKey((prev: number) => prev + 1);
    setShouldTriggerUpload(true);
  };

  const fetchSuiteDataFromCSV = async (
    result: IndividualSuiteParsedFromCSV[]
  ) => {
    await fetchSaveBulkSuitesResponse(result, property.id, user.id);
    await fetchPropertySuites(property.id);
  };

  return (
    <div className="button-right">
      <select
        value={selectedAction}
        className="button button--small button--outline-teal"
        onChange={handleActionChange}
        style={{ minWidth: '160px' }}
      >
        {filteredActionOptionsByRole.map((option: any) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {/* Hidden CSV Download Link. */}
      <div style={{ display: 'none' }}>
        <CSVLink
          ref={csvDownloadRef}
          data={SuiteDummyData}
          headers={SuiteHeaders}
          filename={`${variables.suiteName}.csv`}
          target="_blank"
          data-csv-download="true"
        >
          Download CSV
        </CSVLink>
      </div>
      {/* Hidden CSV Upload Component. */}
      <div style={{ display: 'none' }} ref={csvUploadRef}>
        <CSVUpload
          onFetchData={fetchSuiteDataFromCSV}
          buttonName="Bulk Suites Import"
          key={csvUploadKey}
        />
      </div>
    </div>
  );
};

export default SuitesActionSelect;
